import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormGroup, FormBuilder, ValidationErrors, ValidatorFn, Validators, FormControl } from '@angular/forms';
import { Router } from '@angular/router';

import { Observable, of } from 'rxjs';
import { switchMap, delay, map, first } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { UserService } from 'src/app/services/user/user.service';
import { UpdateCredentialsFormBuilder, StaffLogin } from '@models/index';

@Component({
    selector: 'app-page-credential',
    templateUrl: './page-credential.component.html',
    styleUrls: ['./page-credential.component.scss']
})
export class PageCredentialComponent implements OnInit {

    idStaff!: number;

    typePassword = 'password';
    typeRepeatPassword = 'password';

    errorMssg: string[] = [];

    validateForm!: FormGroup<UpdateCredentialsFormBuilder>;

    isVisiblePassword = false;
    isVisibleRepeatPassword = false;
    userNameIsBusy = false;

    constructor(private userService: UserService, private formBuilder: FormBuilder, private router: Router, private translateService: TranslateService) {
        this.idStaff = this.userService.getIdStaff();
    }

    ngOnInit(): void {
        const staff = this.userService.getStaffData();
        if (!staff?.isTemporalPassword){
            void this.router.navigate(['/schedule']);
        }
        this.loadForm(staff);
    }

    loadForm(staff: StaffLogin | null): void{
        this.validateForm = this.formBuilder.group <UpdateCredentialsFormBuilder>({
            password: new FormControl('', [Validators.required, Validators.minLength(8),
                Validators.pattern('(?=.*[a-zA-Z])(?=.*[0-9]).{8,}')]),
            confirmPass: new FormControl('', Validators.required),
            username: new FormControl(staff?.username ?? '',  { validators: [Validators.required], asyncValidators: [this.checkUsernameInUse], updateOn: 'change' })
        }, { validators: this.checkIfMatchingPasswords('password', 'confirmPass') });
        this.validateForm.valueChanges.subscribe(() => {
            this.getMssgError();
        });
    }

    togglePassword(): void {
        this.isVisiblePassword = !this.isVisiblePassword;
        if (this.isVisiblePassword) {
            this.typePassword = 'text';
        } else {
            this.typePassword = 'password';
        }
    }

    toggleRepeatPassword(): void {
        this.isVisibleRepeatPassword = !this.isVisibleRepeatPassword;
        if (this.isVisibleRepeatPassword) {
            this.typeRepeatPassword = 'text';
        } else {
            this.typeRepeatPassword = 'password';
        }
    }

    getMssgError(): void {
        this.errorMssg = [];
        if (this.validateForm.controls.username.invalid) {
            this.errorMssg.push(this.translateService.instant('username_required'));
        }
        if (this.validateForm.controls.password.invalid) {
            if (this.validateForm.controls.password.value) {
                this.errorMssg.push(this.translateService.instant('password_format_incorrect'));
            } else {
                this.errorMssg.push(this.translateService.instant('password_required'));
            }
        }
        if (this.validateForm.controls.confirmPass.invalid) {
            this.errorMssg.push(this.translateService.instant('contrasena_no_coincide'));
        }
    }

    onSave(): void {
        if (this.validateForm.controls.password.invalid){
            return;
        }
        this.userService.updatePassword(this.idStaff, this.validateForm.value.password ?? '').subscribe(() => {
            this.saveUsername();
        });
    }

    saveUsername(): void {
        if (this.validateForm.controls.username.invalid){
            return;
        }
        this.userService.updateUsername(this.validateForm.value.username ?? '').subscribe(() => {
            void this.router.navigate(['/schedule']);
        });
    }

    checkIfMatchingPasswords(passwordKey: string, passwordConfirmationKey: string): ValidatorFn {
        return (group: AbstractControl): ValidationErrors | null => {
            const passwordInput = group.get(passwordKey);
            const passwordConfirmationInput = group.get(passwordConfirmationKey);

            if (passwordInput && passwordConfirmationInput && passwordInput.value !== passwordConfirmationInput.value) {
                passwordConfirmationInput.setErrors({ notEquivalent: true });

                return { notEquivalent: true };
            } else {
                if (passwordConfirmationInput) {
                    passwordConfirmationInput.setErrors(null);
                }

                return null;
            }
        };
    }
    private checkUsernameInUse = (control: AbstractControl): Observable<ValidationErrors | null> =>
        of(control.value).pipe(
            delay(500),
            switchMap((username) => this.userService.isUsernameAvailableModified(username)
                .pipe(map((response: boolean) => response ? null : { usernameExists: true }))
            ),
            first()
        );
}
